import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import * as msTeams from '@microsoft/teams-js';

import * as accountTypes from '@account/store/types';
import api from '@/api';
import { CardOpenMethod } from './UserCardComponent';
import * as splashHelper  from '@appbase/util/splash-message'
import { createEventBus, removeEventBus } from '@appbase/util';
import * as evtTypes from '@appbase/store/event-types'

export interface PersonPresence{
    guid?: string;
    availability?: 'Available'
            | 'Busy'
            | 'DoNotDisturb'
            | 'BeRightBack'
            | 'Away'
            | 'Offline'
            | 'PresenceUnknown';
    isTeamsUser: boolean;
    personCode: string; 
}

@Component({
    computed: {
        ...mapGetters({
            currentUser: accountTypes.USER,
        }),
    },
})
export default class UserCardExtension extends Vue {
    @Prop({ type: Number }) openMethod!: CardOpenMethod
    @Prop({ type: String, default: '/hr/Person/api/detail' }) identityApiUrl!: string
    @Prop({ type: Function }) addUser!: Function
    @Prop({ type: Function }) sendAlarm!: Function
    @Prop({ type: Function }) addToTeam!: Function
    @Prop({ type: Function }) showDetail!: Function

    // ──────────────────────────────────────────────────────── I ──────────
    //   :::::: C O M P U T E D : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────────────
    //
    get chatUrl() {
        if(this.targetUser == null) {
            return '';
        } else {
            return `https://teams.microsoft.com/l/chat/0/0?users=${this.targetUser.userPrincipalName}&topicName=&message=`;
        }
            
        
    }

    get meetingUrl() {
        if(this.targetUser == null) {
            return '';
        } else {
            return `https://teams.microsoft.com/l/meeting/new?subject=&startTime=&endTime=&content=&attendees=${this.targetUser.userPrincipalName}`;
        }        
    }

    get status() {
        if(this.targetUser == null)
            return '';

        var status = '';
        switch (this.targetUser.availability) {
            case 'Available':
            case 'AvailableIdle':
                status = '대화가능';
                break;
            case 'Away':
            case 'BeRightBack':
                status = '자리비움';
                break;
            case 'Busy':
            case 'BusyIdle':
            case 'DoNotDisturb':
                status = '다른용무중';
                break;
            case 'Offline':
                status = '오프라인';
                break;
            case 'PresenceUnknown':
            default:
                status = '알수없음';
                break;
        }
        return status;
    }

    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: G E T T E R S : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //
    currentUser!: account.User;

    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: D A T A : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //
    targetUser: appbase.account.UserDetail | null = null;
    targetPosition: DOMRect = Object.create(null);
    currentElem: HTMLElement | null = null;  
    photoUrl: string = '';

    isOpen: boolean = false;
    isLoading: boolean = true;
    isPhotoLoading: boolean = true;
    isFocusing: boolean = false;

    styleTop: string = '0';
    styleLeft: string = '0';

    closeTimeout: number | null = null;

    ghostImage: any = require('@appbase/assets/ghost.png');
    ghostLoading: any = require('@appbase/assets/ghost_loading.png');
    lastActivity: string = '';
    availability: string = '';

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: M E T H O D S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //
    openChat() {
        if(this.targetUser == null)
            return;

        if (this.isTeams) {
            if (this.targetUser.isTeamsUser) {
                msTeams.chat.openChat({
                    user: `${this.targetUser.userPrincipalName}` ?? '',
                });
            } else {
                (window as any).alertAsync(this.$t('팀즈_미사용_사용자').toString());
            }
        } else {
            window.open(this.chatUrl);
        }
    }

    openMeeting() {
        if(this.targetUser == null)
            return;

        if (this.isTeams) {
            if (this.targetUser.isTeamsUser) {
                msTeams.app.openLink(this.meetingUrl);
            } else {
                (window as any).alertAsync(this.$t('팀즈_미사용_사용자').toString());
            }
        } else {
            window.open(this.meetingUrl);
        }
    }

    sendMail() {
        if(this.targetUser == null)
            return;

        const uri = `mailto:${this.targetUser.email}`;
        window.open(uri);
    }

    @Watch('targetUser')
    getPhotoPath() {
        if(this.targetUser == null)
            return;

        this.isPhotoLoading = true;
        if(this.targetUser.photoPath){
            this.photoUrl = this.targetUser.photoPath;
        } else {
            this.photoUrl = '/cm/gres/faux/images/ghost.png';
        }
        this.isPhotoLoading = false;
    }

    async setTargetUser(userData: string, type: 'personCode' | 'upn' | 'email') {
        let baseUrl = `${this.identityApiUrl}?`;

        if (type === 'personCode') {
            baseUrl += `personCode=${userData}`;
        } else if (type === 'upn') {
            baseUrl += `upn=${userData}`;
        } else if (type === 'email') {
            baseUrl += `email=${userData}`;
        } else {
            this.targetUser = null;
            return;
        }

        const result: appbase.account.UserDetail | null = await api.get({
          url: baseUrl,
        });

        const activity: Date | null = await api.get({
            url: `/OrgChart/User/api/userActivity`,
            params: {
                personCode: userData
            }
        }).then((res:any)=>{
            if(res){
                return res.lastActivityTime
            }
        })
        
        this.calcActiveTime(activity)
        this.targetUser = result;
        this.availability = this.targetUser?.availability??"Offline"
    }

    setTargetUserAvailability(data: PersonPresence[]){
        if(this.targetUser !== null){
            let updated = data.find(d => d.personCode === this.targetUser?.personCode);
            this.availability = updated?.availability ?? 'Offline';            
        }
    }

    handleOpen(openElem: HTMLElement, userData: string, type: 'personCode' | 'upn' | 'email') {          
        if (!userData) return;                   

        if(this.currentElem == openElem) 
            return;

        let elRect = openElem.getBoundingClientRect();        

        let posTop = elRect.y;
        let posLeft = elRect.x;   
    
        let topDiff = (posTop + 400) - document.body.clientHeight;
        let leftDiff = (posLeft + 336) - document.body.clientWidth;

        if(topDiff > 0) {
            posTop-= topDiff;
        }

        if(leftDiff > 0) {
            posLeft-=leftDiff;
        }
        
        this.styleTop = `${posTop}px`;
        this.styleLeft = `${posLeft}px`;                
        this.setTargetUser(userData, type).then(() => {
            if(this.targetUser != null) {
                this.isOpen = true;
            }            
        });
        (window as any).eventBus.$on(evtTypes.EVENT_PRESENCE_INTERVAL, this.setTargetUserAvailability);
    }

    handleClose(force: boolean) {
        if(force) {
            this.isOpen = false    
        } else {        
            this.closeTimeout = setTimeout(() => {
                this.isOpen = false   
            }, 500);                                            
        }        
    }

    handleMouseLeave(e: MouseEvent) {
        this.isFocusing = false;
        this.handleClose(false);
    }

    onClickClose() {
        this.handleClose(true);
    }

    copyValue(val:string){
        splashHelper.splash("복사되었습니다.",{position_style:'top:50%;left:20%;',fadeout_sec:1.0});
        navigator.clipboard.writeText(val);
    }

    calcActiveTime(lastActivityTime:Date|null) {
        if(lastActivityTime){
            var diff = new Date().getTime() - new Date(lastActivityTime).getTime()
            var time: { sec: number, min: number, hour: number, day: number } = { sec: 0, min: 0, hour: 0, day: 0 }
            if (diff > 0) {
                time.sec = diff / 1000
                time.min = time.sec > 60 ? time.sec / 60 : 0
                time.hour = time.min > 60 ? time.min / 60 : 0
                time.day = time.hour > 24 ? time.hour / 24 : 0
    
                if (time.min > 0) this.lastActivity = `${Math.floor(time.min)}${this.$t('분')}`
                if (time.hour > 0) this.lastActivity = `${Math.floor(time.hour)}${this.$t('시간')}`
                if (time.day > 0) this.lastActivity = `${Math.floor(time.day)}${this.$t('일')}`
            }
            else {
                this.lastActivity = '';
            }
        }
        else {
            this.lastActivity = '';
        }
    }

    beforeDestroy(){
        removeEventBus(window, evtTypes.EVENT_PRESENCE_INTERVAL, this.setTargetUserAvailability )
    }
}
